'use client';

import { c as _c } from "react/compiler-runtime";
import { X, RotateCw } from 'lucide-react';
import { m } from 'motion/react';
import type { AlbumDetails, RotationAlbum } from '@/lib/rotation/types';
import { SpotifyLink } from './SpotifyLink';
export interface Selection {
  album: RotationAlbum;
  back: boolean;
  closing: boolean;
  details: AlbumDetails | null;
  error: string | null;
}
export function AlbumSelection(t0) {
  const $ = _c(48);
  const {
    selection,
    onClose,
    onFlip
  } = t0;
  const {
    album,
    closing
  } = selection;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = closing ? 0 : 1;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      opacity: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = closing ? 0.25 : 0.7;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      duration: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t3 || $[6] !== t5) {
    t6 = {
      initial: t1,
      animate: t3,
      transition: t5
    };
    $[5] = t3;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const fade = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <X size={20} />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== closing || $[10] !== fade || $[11] !== onClose) {
    t8 = <m.button {...fade} className="rotation-dismiss" aria-label="Close album" onClick={onClose} disabled={closing}>{t7}</m.button>;
    $[9] = closing;
    $[10] = fade;
    $[11] = onClose;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== album.title) {
    t9 = <h1>{album.title}</h1>;
    $[13] = album.title;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== album.artist || $[16] !== album.year) {
    t10 = <p>{album.artist} · {album.year}</p>;
    $[15] = album.artist;
    $[16] = album.year;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <dt>In this rotation</dt>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== album.rank) {
    t12 = <div>{t11}<dd>#{album.rank}</dd></div>;
    $[19] = album.rank;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== album.recentPlays) {
    t13 = album.recentPlays !== null && <div><dt>Recent history</dt><dd>{album.recentPlays} {album.recentPlays === 1 ? "play" : "plays"}</dd></div>;
    $[21] = album.recentPlays;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12 || $[24] !== t13) {
    t14 = <dl>{t12}{t13}</dl>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== album.recentPlays) {
    t15 = album.recentPlays !== null && <p className="rotation-data-note">From the last 50 recorded plays.</p>;
    $[26] = album.recentPlays;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== fade || $[29] !== t10 || $[30] !== t14 || $[31] !== t15 || $[32] !== t9) {
    t16 = <m.section {...fade} className="rotation-album-info" aria-label="Album details">{t9}{t10}{t14}{t15}</m.section>;
    $[28] = fade;
    $[29] = t10;
    $[30] = t14;
    $[31] = t15;
    $[32] = t9;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== album) {
    t17 = <SpotifyLink album={album}>Play on Spotify</SpotifyLink>;
    $[34] = album;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <RotateCw size={18} />;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== onFlip) {
    t19 = <button onClick={onFlip} aria-label="Flip album">{t18}</button>;
    $[37] = onFlip;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== closing || $[40] !== fade || $[41] !== t17 || $[42] !== t19) {
    t20 = <m.div {...fade} className="rotation-active-controls" inert={closing}>{t17}{t19}</m.div>;
    $[39] = closing;
    $[40] = fade;
    $[41] = t17;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t16 || $[45] !== t20 || $[46] !== t8) {
    t21 = <>{t8}{t16}{t20}</>;
    $[44] = t16;
    $[45] = t20;
    $[46] = t8;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  return t21;
}